<script setup>
// What a competition shows before anyone is in it.
//
// A member should be told it is still being set up rather than left staring at
// an empty table; an admin should be told what to do next, with the buttons to
// go and do it. Only the words differ between competitions, so this decides the
// words and hands the rest to the house empty state — the same one the app
// draws with TlEmptyState.
//
// Hosts pass their admin actions through the default slot, because only the
// host knows which admin screen sets that competition up.
import NoExistingWithAction from '~/components/NoExistingWithAction.vue';

const props = defineProps({
  // "ladder", "box league", "tournament", "americano" — used in the copy.
  entityTypeName: {type: String, default: 'competition'},
  isAdmin: {type: Boolean, default: false},
  icon: {type: String, default: 'mdi-account-multiple-outline'},
  // What is missing. A cycle needs divisions before it needs players.
  noun: {type: String, default: 'players'},
  // The default admin line is about people joining, which makes no sense when
  // what is missing is divisions. Hosts in that case say their own thing.
  adminHint: {type: String, default: null},
  // Already over. "An admin is still setting this up" is plainly wrong on a
  // competition that finished months ago with nobody in it, and sits right
  // under the banner saying it has ended.
  ended: {type: Boolean, default: false},
  size: {type: String, default: 'large'},
});

// Deliberately silent about players waiting to be placed: on every page that
// can have one, the waiting card sits directly above this, and repeating it
// is the duplication we just took out of the Joining card.

const title = computed(() => {
  if (props.ended) return 'Nothing to show';
  return props.isAdmin ? `No ${props.noun} yet` : 'Nothing to show yet';
});

const blurb = computed(() => {
  if (props.ended) return `This ${props.entityTypeName} ended without anyone taking part.`;
  return props.isAdmin
      ? (props.adminHint ?? `Add ${props.noun} yourself, or share the join link and let them add themselves.`)
      : `An admin is still setting this ${props.entityTypeName} up. Once it is ready, you will see it here.`;
});
</script>

<template>
  <no-existing-with-action :title="title" :blurb="blurb" :icon="icon" :size="size">
    <template v-if="isAdmin && !ended && $slots.default" #actions>
      <slot/>
    </template>
  </no-existing-with-action>
</template>
